import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import AnalyticsFilterBar from './AnalyticsFilterBar';
import { toQuery, useAnalyticsFilters, withFilters } from './filters';
import { BarChart, StackedBar } from './charts';
import { deltaPct, fmtDate, fmtMin, fmtNum, fmtPct } from './format';
import { Kpi, Tile, alert, card, cardHead, grid, muted, td, tdR, th, thR } from './Overview';

// "Linie" (spec 7.3): full picture of the selected line on top, ranking of all lines below.
// Clicking a ranking row switches the top view.

interface Summary {
  line: { id: number; name: string; external_code: string | null };
  kpi: Kpi; previous: Kpi | null;
  series: (Kpi & { date: string })[];
  byReason: { reason: string; minutes: number; count: number }[];
  shifts: (Kpi & { shift: string; operators: { id: number; name: string; pcs: number; kg: number }[] })[];
  materials: (Kpi & { key: string; label: string; shareKg: number | null })[];
}
interface Ranking { lines: (Kpi & { id: number; name: string; external_code: string | null })[] }

const Lines: React.FC = () => {
  const { t } = useLanguage();
  const [filters, setFilters] = useAnalyticsFilters();
  const [params] = useSearchParams();
  const [ranking, setRanking] = useState<Ranking | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [selected, setSelected] = useState<string>(filters.lineId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ranking ignores the line filter on purpose — it is the list to pick from.
  useEffect(() => {
    const { line_id, ...rest } = toQuery({ ...filters, compare: false });
    api.get('/analytics/lines/ranking', { params: rest })
      .then(res => { setRanking(res.data); if (!selected) { const first = res.data.lines.find((l: Kpi) => l.hasTelemetry) ?? res.data.lines[0]; if (first) setSelected(String(first.id)); } })
      .catch(err => setError(err?.response?.data?.message ?? t('analytics.loadError')));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.preset, filters.customFrom, filters.customTo, filters.shift, filters.materialId, t]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setLoading(true);
    const { line_id, ...rest } = toQuery(filters);
    api.get(`/analytics/lines/${selected}/summary`, { params: rest })
      .then(res => { if (!cancelled) { setSummary(res.data); setError(null); } })
      .catch(err => { if (!cancelled) setError(err?.response?.data?.message ?? t('analytics.loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selected, filters, t]);

  const k = summary?.kpi;
  const p = summary?.previous ?? null;
  const d = (cur: number | null | undefined, prev: number | null | undefined) => (filters.compare && p ? deltaPct(cur, prev) : null);
  const reasonLabel = (r: string) => (r === 'no_speed' || r === 'filament_lost' ? t(`analytics.reason_${r}`) : r);
  const historyLink = withFilters('/analityka/historia-linii', new URLSearchParams({ ...Object.fromEntries(params), line: selected }));

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>🏭 {t('lines.title')}</h1>
        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>{t('lines.subtitle')}</p>
      </div>
      <AnalyticsFilterBar filters={filters} onChange={f => { setFilters(f); if (f.lineId !== undefined) setSelected(f.lineId); }} />
      {error && <div style={alert}>{error}</div>}

      {summary && k && (
        <div style={{ opacity: loading ? 0.6 : 1 }}>
          <div style={{ ...cardHead, marginBottom: 10 }}>
            <h2 style={{ margin: 0, fontSize: 20, color: '#1e293b' }}>{t('lines.lineHeader', { name: summary.line.name })} <span style={muted}>{summary.line.external_code}</span></h2>
            <Link to={historyLink} style={{ fontSize: 13, color: '#667eea', fontWeight: 600 }}>{t('lines.goHistory')} →</Link>
          </div>
          <div style={grid}>
            <Tile label={t('analytics.kpiNetKg')} value={`${fmtNum(k.kgNet, 1)} kg`} sub={`${fmtNum(k.pcs)} ${t('analytics.pcs')}`} delta={d(k.kgNet, p?.kgNet)} vsLabel={t('analytics.vsPrev')} />
            <Tile label={t('analytics.kpiFlowKg')} value={`${fmtNum(k.kgFlow, 1)} kg`} sub={t('analytics.kpiFlowKgSub', { count: fmtNum(k.cycles) })} delta={d(k.kgFlow, p?.kgFlow)} vsLabel={t('analytics.vsPrev')} />
            <Tile label={t('analytics.colProd')} value={fmtMin(k.prodMin)} delta={d(k.prodMin, p?.prodMin)} vsLabel={t('analytics.vsPrev')} />
            <Tile label="Availability" value={fmtPct(k.availability)} delta={d(k.availability, p?.availability)} vsLabel={t('analytics.vsPrev')} bar={k.availability} />
            <Tile label={t('analytics.kpiAvgSpeed')} value={k.avgSpeed === null ? '—' : `${fmtNum(k.avgSpeed, 1)} m/min`} sub={k.tgtSpeed ? t('analytics.target', { value: fmtNum(k.tgtSpeed, 0) }) : t('analytics.noTargets')} delta={d(k.avgSpeed, p?.avgSpeed)} vsLabel={t('analytics.vsPrev')} />
            <Tile label="Performance" value={fmtPct(k.performance)} delta={d(k.performance, p?.performance)} vsLabel={t('analytics.vsPrev')} bar={k.performance} />
            <Tile label={t('analytics.kpiDowntime')} value={fmtMin(k.downMin)} sub={t('analytics.kpiDowntimeSub', { count: fmtNum(k.downCnt), avg: fmtMin(k.avgDownMin) })} delta={d(k.downMin, p?.downMin)} invert vsLabel={t('analytics.vsPrev')} />
            <Tile label={t('analytics.kpiShortStops')} value={String(k.ssCnt)} sub={t('analytics.kpiShortStopsSub', { count: fmtNum(k.ssCnt), rate: fmtNum(k.stopsPerProdH, 2) })} delta={d(k.ssCnt, p?.ssCnt)} invert vsLabel={t('analytics.vsPrev')} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
            {[
              { title: 'kg', data: summary.series.map(s => ({ label: fmtDate(s.date), value: s.kgNet })), color: '#2f80ed' },
              { title: t('analytics.colSpeed'), data: summary.series.map(s => ({ label: fmtDate(s.date), value: s.avgSpeed })), color: '#0f9d58' },
              { title: t('analytics.colDown'), data: summary.series.map(s => ({ label: fmtDate(s.date), value: s.downMin })), color: '#6b7280' },
              { title: 'Availability', data: summary.series.map(s => ({ label: fmtDate(s.date), value: s.availability === null ? null : s.availability * 100 })), color: '#667eea' },
            ].map(c => (
              <div key={c.title} style={{ ...card, marginBottom: 0, padding: '10px 12px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>{c.title}</div>
                <BarChart data={c.data} color={c.color} height={140} format={v => fmtNum(v, 0)} />
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 16 }}>
            <div style={card}>
              <div style={cardHead}><b>{t('analytics.timeStructure')}</b></div>
              <StackedBar format={fmtMin} parts={[
                { label: t('analytics.stProd'), value: k.prodMin, color: '#2f80ed' },
                { label: t('analytics.stShort'), value: k.ssMin, color: '#e8590c' },
                { label: t('analytics.stDown'), value: k.otMin, color: '#6b7280' },
                { label: t('analytics.stNoData'), value: k.noDataMin, color: '#d1d5db' },
              ]} />
              <div style={{ fontWeight: 700, fontSize: 13, margin: '16px 0 8px' }}>{t('downtime.byReason')}</div>
              {summary.byReason.length === 0 ? <div style={muted}>—</div> : summary.byReason.map(r => (
                <div key={r.reason} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span>{reasonLabel(r.reason)}</span><span><b>{fmtMin(r.minutes)}</b> <span style={muted}>{r.count}×</span></span>
                </div>
              ))}
            </div>
            <div style={card}>
              <div style={cardHead}><b>{t('lines.shifts')}</b><span style={muted}>{t('lines.shiftsSub')}</span></div>
              {summary.shifts.map(s => (
                <div key={s.shift} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <b>{t(s.shift === 'zmiana_1' ? 'analytics.shift1' : 'analytics.shift2')}</b>
                    <span>{fmtMin(s.prodMin)} · {fmtNum(s.kgNet, 1)} kg · {fmtNum(s.avgSpeed, 1)} m/min · Avail. {fmtPct(s.availability)}</span>
                  </div>
                  <div style={{ ...muted, marginTop: 4 }}>
                    {s.operators.length === 0 ? t('lines.noOperators') : s.operators.map(o => `${o.name} (${o.pcs} ${t('analytics.pcs')})`).join(', ')}
                  </div>
                </div>
              ))}
            </div>
            <div style={card}>
              <div style={cardHead}><b>{t('lines.materials')}</b></div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#f8f9fa' }}><th style={th}>{t('standards.colMaterial')}</th><th style={thR}>{t('analytics.colProd')}</th><th style={thR}>kg</th><th style={thR}>{t('analytics.colSpeed')}</th><th style={thR}>Perf.</th></tr></thead>
                <tbody>
                  {summary.materials.map(m => (
                    <tr key={m.key}><td style={{ ...td, fontWeight: 600 }}>{m.label}</td><td style={tdR}>{fmtMin(m.prodMin)}</td><td style={tdR}>{fmtNum(m.kgNet, 1)}</td><td style={tdR}>{fmtNum(m.avgSpeed, 1)}</td><td style={tdR}>{fmtPct(m.performance)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div style={card}>
        <div style={cardHead}><b>{t('lines.ranking')}</b><span style={muted}>{t('lines.rankingSub')}</span></div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={th}>#</th><th style={th}>{t('analytics.colLine')}</th><th style={thR}>{t('analytics.colKg')}</th><th style={thR}>{t('analytics.colPcs')}</th><th style={thR}>{t('analytics.colProd')}</th><th style={thR}>Availability</th><th style={thR}>{t('analytics.colSpeed')}</th><th style={thR}>Performance</th><th style={thR}>{t('analytics.colDown')}</th><th style={thR}>kg/h</th>
              </tr>
            </thead>
            <tbody>
              {!ranking ? <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 24 }}>{t('common.loading')}</td></tr>
                : [...ranking.lines].sort((a, b) => b.kgNet - a.kgNet || b.prodMin - a.prodMin).map((l, i) => (
                  <tr key={l.id} onClick={() => setSelected(String(l.id))} style={{ cursor: 'pointer', background: selected === String(l.id) ? '#eef2ff' : undefined }}>
                    <td style={{ ...td, color: '#94a3b8' }}>{i + 1}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{l.name} {!l.hasTelemetry && <span style={{ ...muted, fontWeight: 400 }}>· {t('analytics.noTelemetry')}</span>}</td>
                    <td style={{ ...tdR, fontWeight: 700 }}>{fmtNum(l.kgNet, 1)}</td><td style={tdR}>{fmtNum(l.pcs)}</td>
                    <td style={tdR}>{l.hasTelemetry ? fmtMin(l.prodMin) : '—'}</td><td style={tdR}>{fmtPct(l.availability)}</td><td style={tdR}>{fmtNum(l.avgSpeed, 1)}</td><td style={tdR}>{fmtPct(l.performance)}</td>
                    <td style={tdR}>{l.hasTelemetry ? fmtMin(l.downMin) : '—'}</td><td style={tdR}>{fmtNum(l.kgPerH, 1)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Lines;
